/**
 * 대시보드 메시지 유틸리티 함수들
 */

/**
 * 성공 메시지 표시 함수
 * @param {string} message - 표시할 성공 메시지
 */
export const showSuccessMessage = (message) => {
  // 배경 오버레이 생성
  const overlay = document.createElement('div');
  overlay.className = 'custom-alert-overlay';
  
  // 메시지 요소 생성
  const messageElement = document.createElement('div');
  messageElement.className = 'custom-alert success';
  messageElement.innerHTML = `
    <div class="custom-alert-content">
      <p>${message}</p>
      <button class="confirm-btn">확인</button>
    </div>
  `;
  
  // 오버레이에 메시지 추가
  overlay.appendChild(messageElement);
  document.body.appendChild(overlay);
  
  // 확인 버튼 이벤트 핸들러
  const confirmBtn = messageElement.querySelector('.confirm-btn');
  confirmBtn.addEventListener('click', () => {
    overlay.remove();
  });
  
  // 오버레이 클릭 시 닫기
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      overlay.remove();
    }
  });
  
  // 5초 후 자동으로 닫기
  setTimeout(() => {
    if (document.body.contains(overlay)) {
      overlay.remove();
    }
  }, 5000);
};

/**
 * 에러 메시지 표시 함수
 * @param {string} message - 표시할 에러 메시지
 */
export const showErrorMessage = (message) => {
  // 배경 오버레이 생성
  const overlay = document.createElement('div');
  overlay.className = 'custom-alert-overlay';
  
  // 메시지 요소 생성
  const messageElement = document.createElement('div');
  messageElement.className = 'custom-alert error';
  messageElement.innerHTML = `
    <div class="custom-alert-content">
      <p>${message}</p>
      <button class="confirm-btn">확인</button>
    </div>
  `;
  
  // 오버레이에 메시지 추가
  overlay.appendChild(messageElement);
  document.body.appendChild(overlay);
  
  // 확인 버튼 이벤트 핸들러
  const confirmBtn = messageElement.querySelector('.confirm-btn');
  confirmBtn.addEventListener('click', () => {
    overlay.remove();
  });
  
  // 오버레이 클릭 시 닫기
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      overlay.remove();
    }
  });
};

/**
 * 확인 메시지 표시 함수
 * @param {string} message - 표시할 확인 메시지
 * @param {Function} onConfirm - 확인 버튼 클릭 시 실행할 콜백 함수
 * @param {Function} onCancel - 취소 버튼 클릭 시 실행할 콜백 함수
 */
export const showConfirmMessage = (message, onConfirm, onCancel) => {
  // 배경 오버레이 생성
  const overlay = document.createElement('div');
  overlay.className = 'custom-alert-overlay';
  
  // 메시지 요소 생성
  const messageElement = document.createElement('div');
  messageElement.className = 'custom-alert confirm';
  messageElement.innerHTML = `
    <div class="custom-alert-content">
      <p>${message}</p>
      <div class="confirm-actions">
        <button class="confirm-yes-btn">예</button>
        <button class="confirm-no-btn">아니오</button>
      </div>
    </div>
  `;
  
  // 오버레이에 메시지 추가
  overlay.appendChild(messageElement);
  document.body.appendChild(overlay);
  
  // 확인 버튼 이벤트 핸들러
  const confirmYesBtn = messageElement.querySelector('.confirm-yes-btn');
  confirmYesBtn.addEventListener('click', () => {
    overlay.remove();
    if (onConfirm) onConfirm();
  });
  
  // 취소 버튼 이벤트 핸들러
  const confirmNoBtn = messageElement.querySelector('.confirm-no-btn');
  confirmNoBtn.addEventListener('click', () => {
    overlay.remove();
    if (onCancel) onCancel();
  });
  
  // 오버레이 클릭 시 닫기
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      overlay.remove();
      if (onCancel) onCancel();
    }
  });
}; 

/**
 * 입력 필드를 포함한 확인 프롬프트 표시 함수
 * @param {string} message - 확인 메시지(선택)
 * @param {Array} inputFields - 입력 필드 정의 배열 [{ id, label, type: 'text'|'textarea', required, placeholder, default }]
 * @param {Function} onConfirm - (values) => void, 확인 시 각 입력값을 id 키로 가지는 객체 전달
 * @param {Function} onCancel - 취소 콜백
 */
export const showConfirmPrompt = (message, inputFields = [], onConfirm, onCancel) => {
  const overlay = document.createElement('div');
  overlay.className = 'custom-alert-overlay';

  const messageElement = document.createElement('div');
  messageElement.className = 'custom-alert confirm';

  // 단일 메모 필드로 고정
  const safeFields = (Array.isArray(inputFields) && inputFields.length > 0) ? inputFields : [{ id: 'note', label: '처리 메모', placeholder: '메모를 입력하세요', required: false, default: '' }];
  const f0 = safeFields[0] || { id: 'note', label: '처리 메모', placeholder: '', required: false, default: '' };
  const fieldId = String(f0.id || 'note');
  const label = String(f0.label || '처리 메모');
  const placeholder = f0.placeholder ? String(f0.placeholder) : '메모를 입력하세요';
  const defaultValue = f0.default != null ? String(f0.default) : '';
  const requiredAttr = f0.required ? 'data-required="true"' : '';

  messageElement.innerHTML = `
    <div class="custom-alert-content">
      ${message ? `<h3 class="confirm-title">${message}</h3>` : ''}
      <div class="note-prompt">
        <div class="note-label-chip">${label}</div>
        <textarea id="cf_${fieldId}" data-field-id="${fieldId}" ${requiredAttr} class="note-textarea" placeholder="${placeholder}">${defaultValue}</textarea>
      </div>
      <div class="confirm-actions">
        <button class="confirm-yes-btn btn-green">예</button>
        <button class="confirm-no-btn btn-red">아니오</button>
      </div>
    </div>
  `;

  overlay.appendChild(messageElement);
  document.body.appendChild(overlay);

  const collectValues = () => {
    const values = {};
    const fields = messageElement.querySelectorAll('[data-field-id]');
    let hasError = false;
    fields.forEach((el) => {
      const id = el.getAttribute('data-field-id');
      const def = safeFields.find(f => (String(f.id || '').trim() || 'note') === id) || {};
      const val = (el.tagName === 'TEXTAREA' ? el.value : el.value);
      // required 검사
      const isRequired = !!def.required || el.getAttribute('data-required') === 'true';
      if (isRequired && (!val || String(val).trim() === '')) {
        hasError = true;
        el.classList.add('input-error');
      } else {
        el.classList.remove('input-error');
      }
      values[id] = val;
    });
    return { values, hasError };
  };

  const confirmYesBtn = messageElement.querySelector('.confirm-yes-btn');
  confirmYesBtn.addEventListener('click', () => {
    const { values, hasError } = collectValues();
    if (hasError) {
      // 필수 입력값 누락 시 닫지 않고 유지
      return;
    }
    overlay.remove();
    if (onConfirm) onConfirm(values);
  });

  const confirmNoBtn = messageElement.querySelector('.confirm-no-btn');
  confirmNoBtn.addEventListener('click', () => {
    overlay.remove();
    if (onCancel) onCancel();
  });

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      overlay.remove();
      if (onCancel) onCancel();
    }
  });
};